import { Outlet } from 'react-router-dom';
import { LoadingSpinner } from '@demand-iq/ui';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import NotFound from 'pages/NotFound';

type RequireFeatureFlagProps = Readonly<{
  flag: string;
}>;

/**
 * Layout route that gates a whole subtree on a PostHog feature flag:
 *
 *   <Route element={<RequireFeatureFlag flag="canonry" />}>
 *     <Route path={miaCanonryDashboard} element={...} />
 *   </Route>
 *
 * Gating the route rather than the page means a flagged-out visitor never mounts the page at all,
 * so none of its queries fire on the way out. Mounting it as a layout route keeps App.tsx flat.
 *
 * `useFeatureFlagEnabled` returns `undefined` until PostHog answers and portal does not bootstrap
 * flags, so -- exactly like RequireApp's `isPending` branch -- we hold rather than reject while the
 * answer is outstanding. Redirecting on `undefined` would bounce a flagged-in user off the route on
 * every hard refresh or direct link.
 *
 * A flagged-out visitor gets a 404 rather than a redirect. Bouncing them to "/" was itself a tell:
 * a bookmark that redirects is a bookmark that pointed at something real, so the gate advertised
 * the surface it was meant to hide. The 404 also keeps the URL they typed, so flipping the flag on
 * makes a reload work (ENG-1169).
 */
const RequireFeatureFlag = ({ flag }: RequireFeatureFlagProps): JSX.Element => {
  const enabled = useFeatureFlagEnabled(flag);

  if (enabled === undefined) {
    return <LoadingSpinner className="h-64" />;
  }

  if (!enabled) {
    return <NotFound inShell />;
  }

  return <Outlet />;
};

export default RequireFeatureFlag;
